/**
 * Render quality tiers, and the governor that owns the current tier. The tier is what was chosen (the device default,
 * a preset in Settings → Graphics, the perf overlay's q button) and stays there: automatic stepping to hold the frame
 * rate — scaling the render resolution inside the tier, then stepping down a tier — ships OFF and only runs with
 * `?autoq` (QualityGovernor.locked).
 *
 * Every tier keeps the same scene: the same models, ground, props and effects. What the tiers trade is the
 * per-pixel cost, which is where a phone's GPU spends its frame: render resolution, screen-space ambient occlusion
 * (a second full scene pass plus 12 samples a pixel), the soft-shadow filter and its map size, MSAA and the bloom
 * chain. Ordered by what the eye misses least. Where a tier has no MSAA, FXAA folded into the post chain's one output
 * pass can take over (lighting review F10) — but FXAA is vetoed on phones (`fxaaVetoed`), so there every tier below
 * ultra (or below an MSAA override) runs without antialiasing, as does `lowest` everywhere.
 */
import { QUALITY_TIERS, QualityTier, defaultTier, pinnedTier, tierIndex } from "./qualityTiers";

export * from "./qualityTiers";

/** Where a device starts: a `?q=<tier>` pin, else the saved tier (see QualityGovernor.resume), else the device default. */
export function startingTier(store: TierStore = localTierStore, now = Date.now()): number {
  const pin = pinnedTier();
  if (pin !== null) return pin;
  return QualityGovernor.resume(store.read(), defaultTier(), now);
}

/** What the governor remembers between launches. `manual` is a choice from the perf overlay: it never expires. */
export interface SavedTier { tier: string; at: number; manual?: boolean }

/** Persistence for the governor, injectable for tests. */
export interface TierStore { read(): SavedTier | null; write(s: SavedTier): void; clear?(): void }

export const localTierStore: TierStore = {
  read(): SavedTier | null {
    try {
      const v = localStorage.getItem(QualityGovernor.Key);
      if (v === null) return null;
      if (/^\d+$/.test(v)) {
        // A bare index, written by the governor before saves carried a time: read it as expired, so the next launch
        // starts one step better (never better than the device default) and the governor re-tests from there.
        const t = QUALITY_TIERS[Math.min(QUALITY_TIERS.length - 1, Number(v))];
        return { tier: t.name, at: 0 };
      }
      const s = JSON.parse(v) as Partial<SavedTier>;
      return typeof s.tier === "string" && typeof s.at === "number" ? { tier: s.tier, at: s.at, manual: s.manual === true } : null;
    } catch { return null; }
  },
  write(s: SavedTier): void { try { localStorage.setItem(QualityGovernor.Key, JSON.stringify(s)); } catch { /* private mode */ } },
  clear(): void { try { localStorage.removeItem(QualityGovernor.Key); } catch { /* private mode */ } },
};

/** Persistence while a `?q=` pin is on: nothing is read or written, so a capture never changes the player's saved tier. */
export const noTierStore: TierStore = { read: () => null, write: () => {}, clear: () => {} };
/** Where the governor keeps its tier: nowhere while `?q=` pins it (see pinnedTier), else localStorage. */
export function tierStoreFor(search?: string): TierStore { return pinnedTier(search) === null ? localTierStore : noTierStore; }

/**
 * True when the device starts on its recommended tier: nothing pinned by `?q=`, no manual pick saved, and no saved
 * automatic step-down that resumes below the default (the sheet would otherwise say "Recommended · high" while the
 * device runs `low`).
 */
export function followsDefaultTier(store: TierStore = localTierStore, search?: string, now = Date.now()): boolean {
  if (pinnedTier(search) !== null) return false;
  const saved = store.read();
  if (saved?.manual === true) return false;
  const fallback = defaultTier();
  return QualityGovernor.resume(saved, fallback, now) === fallback;
}

/**
 * Watches frame intervals and keeps the device at ~60 fps with the least visible loss.
 *
 * It judges a window by its MEDIAN frame, so a shader compile, a garbage collection or a burst of spawns (a handful
 * of long frames) cannot condemn it; and a tier is only given up after two slow windows in a row with the render
 * scale already at its floor. Before that it trades resolution inside the tier (`scale`, applied to the post chain's
 * render target, so the canvas and its compositing never change size), which is continuous and cheap to undo.
 *
 * The scale probes back up after a run of good windows, since a browser caps requestAnimationFrame at the display
 * rate and spare GPU time is otherwise invisible; a probe that fails backs off for longer each time. Tiers only ever
 * step DOWN, and the settled tier is remembered, so the next launch starts where this one ended.
 *
 * Frames whose JavaScript alone takes most of the budget are CPU-bound: fewer pixels would not help them, so those
 * windows neither lower the scale nor count against the tier.
 *
 * It also does not punish (lighting review F10):
 * - **A 30 Hz cap.** iOS Low Power Mode (and some WebViews when hot) caps requestAnimationFrame at 30 Hz: every
 *   interval is 33.3 ms with almost no jitter. A window whose intervals sit at 33.3 ± 2 ms with a small spread is
 *   treated as capped and counts neither way. Caveat, accepted: a GPU that genuinely needs 17–33 ms a frame also
 *   snaps to a steady 33.3 under vsync; the project's floor is 30 fps, so holding a steady 30 is no reason to drop.
 * - **Forever.** A step-down is saved with its time; after 24 h the next launch starts one tier better (never better
 *   than the device default). A manual pick from the perf overlay never expires.
 * - **Without a second chance.** Once per session, after a step-down, if the device then holds 60 comfortably at full
 *   scale (p95 under 17.5 ms) for ProbeWindows windows, it steps back up one tier; a slow window on probation sends
 *   it straight back, and the probe is spent.
 */
export class QualityGovernor {
  /** A window whose median frame is slower than this (ms, ~54 fps) is too heavy. */
  static readonly SlowMs = 18.5;
  /** A window whose median frame is at or under this (ms) is holding 60 with room. */
  static readonly GoodMs = 17.2;
  /**
   * Median JavaScript time per frame before rendering (ms) past which the frame is CPU-bound: with the render's own
   * submission on top (3 ms and up on a phone) that alone misses 60, whatever the pixel count.
   */
  static readonly CpuBoundMs = 10;
  /** Length of a measuring window, and the grace after a change or a hitch before measuring again. */
  static readonly WindowMs = 2000;
  static readonly SettleMs = 1500;
  /** A gap this long is a load or a tab switch, not rendering: it restarts the window rather than counting. */
  static readonly HitchMs = 250;
  /** Render-scale floor and step. At high's 1.5x the floor is ~1.05x: below that, a tier's cheaper effects are the better trade. */
  static readonly MinScale = 0.7;
  static readonly ScaleStep = 0.1;
  /** Slow windows at the scale floor before a tier is given up. */
  static readonly Strikes = 2;
  /** Good windows before the scale probes back up; doubles after each failed probe, up to the cap. */
  static readonly ProbeAfter = 4;
  static readonly ProbeCap = 32;
  /** M42: renamed with the game (it was "td.quality.v3", and "td.quality" before that, when the old governor saved "lowest" on transient hitches). */
  static readonly Key = "highmarch.quality";
  /** A 30 Hz cap: mean interval within this of 33.3 ms, and a standard deviation under CapJitterMs. */
  static readonly CapMs = 1000 / 30;
  static readonly CapToleranceMs = 2;
  static readonly CapJitterMs = 2.5;
  /** A saved step-down older than this is retried one tier better. */
  static readonly ExpireMs = 24 * 60 * 60 * 1000;
  /** The tier up-probe: p95 under this, at full scale, sustained for ProbeWindows windows after a step down. */
  static readonly ProbeP95Ms = 17.5;
  static readonly ProbeWindows = 12;

  /**
   * Automatic stepping is OFF (Kevin, 2026-09-22): the governor was dropping the tier and the render scale on the
   * phone whenever a frame ran long, so play-testing kept landing on "lowest" with no way to tell whether the scene
   * had actually got heavier. The tier is now whatever was chosen — the phone's default or the overlay's q button —
   * and stays there. `frame` still records nothing when locked, and Automatic can be turned back on with `?autoq`.
   */
  locked = !(typeof location !== "undefined" && new URLSearchParams(location.search).has("autoq"));
  private last = 0;
  private windowStart = 0;
  private readonly intervals: number[] = [];
  private readonly busy: number[] = [];
  private quietUntil = 0;
  private strikes = 0;
  private good = 0;
  private probeAfter = QualityGovernor.ProbeAfter;
  private probing = false;
  private renderScale = 1;
  /** Tier up-probe: -1 none yet; 0 armed by a step down; 1 on probation one tier up; 2 spent this session. */
  private tierProbe = -1;
  private tierFast = 0;

  /**
   * `rescale` applies a render-scale change within the tier (1 = the tier's own resolution); `store` and `clock` are
   * where and when step-downs are saved (injectable for tests).
   */
  constructor(private tier: number, private readonly apply: (tier: QualityTier) => void, private readonly rescale: (scale: number) => void = () => {},
    private readonly store: TierStore = localTierStore, private readonly clock: () => number = Date.now) {
    this.apply(QUALITY_TIERS[this.tier]);
  }

  /**
   * The tier to start at given what was saved: a manual pick as is; an automatic step-down as is for 24 h, then one
   * tier better (never better than the device default: an expired save must not put a phone on `ultra`).
   */
  static resume(saved: SavedTier | null, fallback: number, now: number): number {
    if (!saved) return fallback;
    const i = tierIndex(saved.tier);
    if (i < 0) return fallback;
    if (saved.manual) return i;
    if (now - saved.at >= QualityGovernor.ExpireMs) return Math.max(fallback, i - 1);
    return Math.max(fallback, i);
  }

  get current(): QualityTier { return QUALITY_TIERS[this.tier]; }
  get index(): number { return this.tier; }
  /** The render-resolution scale within the tier. */
  get scale(): number { return this.renderScale; }

  /**
   * Whether the tier is the device's recommended one (defaultTier) rather than a pick: true until `set`, and again
   * after `useDefault`. The scene seeds it from what was saved (followsDefaultTier).
   */
  followsDefault = true;

  /** Settings → Graphics → Recommended: back to the device's default tier, forgetting any saved pick. */
  useDefault(tier: number = defaultTier()): void {
    this.tier = Math.max(0, Math.min(QUALITY_TIERS.length - 1, tier));
    this.followsDefault = true;
    this.locked = true;
    this.setScale(1);
    this.apply(this.current);
    this.store.clear?.();
  }

  /** Manual choice from the perf overlay or Settings → Graphics: applies it and stops the governor second-guessing it. */
  set(tier: number): void {
    this.followsDefault = false;
    this.tier = Math.max(0, Math.min(QUALITY_TIERS.length - 1, tier));
    this.locked = true;
    this.setScale(1);
    this.apply(this.current);
    this.store.write({ tier: this.current.name, at: this.clock(), manual: true });
  }

  /** Call once per rendered frame: `now` the frame's start, `busyMs` the JavaScript it ran before rendering (if known). */
  frame(now: number, busyMs = 0): void {
    const dt = now - this.last;
    this.last = now;
    if (this.locked || now < this.quietUntil) { this.restart(now); return; }
    if (dt > QualityGovernor.HitchMs) { this.restart(now); this.quietUntil = now + QualityGovernor.SettleMs; return; }
    this.intervals.push(dt); this.busy.push(busyMs);
    if (now - this.windowStart < QualityGovernor.WindowMs) return;
    const frameMs = median(this.intervals), jsMs = median(this.busy);
    const capped = capped30(this.intervals), p95 = percentile(this.intervals, 0.95);
    this.restart(now);
    if (capped) return;                       // a steady 30 Hz cap (Low Power Mode): counts neither way
    if (frameMs > QualityGovernor.SlowMs) this.slow(now, jsMs > QualityGovernor.CpuBoundMs);
    else if (frameMs <= QualityGovernor.GoodMs) this.fine(now, p95);
  }

  private slow(now: number, cpuBound: boolean): void {
    this.good = 0;
    this.tierFast = 0;
    if (this.tierProbe === 1) {               // on probation one tier up: straight back down, and the probe is spent
      this.tierProbe = 2;
      this.stepTier(+1, now, true);
      return;
    }
    if (this.probing) {                       // the last step up was too much: back off and wait longer next time
      this.probing = false;
      this.probeAfter = Math.min(QualityGovernor.ProbeCap, this.probeAfter * 2);
    }
    if (cpuBound) return;
    if (this.renderScale > QualityGovernor.MinScale + 1e-3) {
      this.setScale(this.renderScale - QualityGovernor.ScaleStep);
    } else if (++this.strikes >= QualityGovernor.Strikes && this.tier < QUALITY_TIERS.length - 1) {
      this.strikes = 0;
      if (this.tierProbe === -1) this.tierProbe = 0;
      this.stepTier(+1, now, true);
    }
    this.quietUntil = now + QualityGovernor.SettleMs;
  }

  private stepTier(dir: number, now: number, persist: boolean): void {
    this.followsDefault = false;              // an automatic step is no longer the recommended tier
    this.tier = Math.max(0, Math.min(QUALITY_TIERS.length - 1, this.tier + dir));
    this.setScale(1);
    this.apply(this.current);
    if (persist) this.store.write({ tier: this.current.name, at: this.clock() });
    this.quietUntil = now + QualityGovernor.SettleMs;
  }

  private fine(now: number, p95: number): void {
    this.strikes = 0;
    if (this.renderScale >= 1) {
      if (this.tierProbe === 1) {
        if (++this.tierFast >= QualityGovernor.ProbeWindows) { this.tierProbe = 2; this.store.write({ tier: this.current.name, at: this.clock() }); }
      } else if (this.tierProbe === 0 && this.tier > 0 && p95 < QualityGovernor.ProbeP95Ms) {
        if (++this.tierFast >= QualityGovernor.ProbeWindows) { this.tierFast = 0; this.tierProbe = 1; this.stepTier(-1, now, false); return; }
      } else this.tierFast = 0;
    }
    if (this.probing) { this.probing = false; this.probeAfter = QualityGovernor.ProbeAfter; }
    if (this.renderScale >= 1 || ++this.good < this.probeAfter) return;
    this.good = 0;
    this.probing = true;
    this.setScale(this.renderScale + QualityGovernor.ScaleStep);
  }

  private setScale(s: number): void {
    const next = Math.min(1, Math.max(QualityGovernor.MinScale, Math.round(s * 100) / 100));
    if (next === this.renderScale) return;
    this.renderScale = next;
    this.rescale(next);
  }

  private restart(now: number): void { this.windowStart = now; this.intervals.length = 0; this.busy.length = 0; }
}

function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[s.length >> 1];
}

function percentile(xs: number[], q: number): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.floor(s.length * q))];
}

/** A steady 30 Hz cap: mean interval within CapToleranceMs of 33.3 ms and a standard deviation under CapJitterMs. */
function capped30(xs: number[]): boolean {
  if (xs.length === 0) return false;
  let sum = 0;
  for (const x of xs) sum += x;
  const mean = sum / xs.length;
  let v = 0;
  for (const x of xs) v += (x - mean) * (x - mean);
  const sd = Math.sqrt(v / xs.length);
  return Math.abs(mean - QualityGovernor.CapMs) <= QualityGovernor.CapToleranceMs && sd <= QualityGovernor.CapJitterMs;
}
